import { chartLayoutColours } from '@risksmart-app/components/src/utils/colours';
import type Highcharts from 'highcharts';

import { toExportFilename } from '../../../../../utils/exportFilename';

/**
 * Image formats the indicator results chart can be exported to, matching the
 * dashboard line-chart widget's PNG / JPEG / SVG options (CUS-145).
 */
export type ChartExportFormat = 'jpeg' | 'png' | 'svg';

const MIME_TYPE_BY_FORMAT: Record<
  ChartExportFormat,
  Highcharts.ExportingMimeTypeValue
> = {
  png: 'image/png',
  jpeg: 'image/jpeg',
  svg: 'image/svg+xml',
};

/**
 * Exports the indicator results chart as an immediate image download in the
 * requested format (CUS-145). The export re-renders the chart off-screen with
 * the indicator name as its title, at the chart's live on-screen size —
 * Highcharts' default 2x export scale plus RSHighcharts' rem-pinned axis
 * labels keep raster output legible for board-pack use (PRO-521). PNG/JPEG
 * rasterise, SVG stays vector — the same client-side offline-exporting path
 * the dashboard widget uses. Returns false when no chart is mounted (e.g. the
 * empty state or a non-numeric indicator) so the caller can skip side-effects.
 */
export const exportResultsChart = (
  chart: Highcharts.Chart | null | undefined,
  indicatorName: string,
  format: ChartExportFormat
): boolean => {
  if (!chart) {
    return false;
  }

  chart.exporting.exportChart(
    {
      type: MIME_TYPE_BY_FORMAT[format],
      filename: toExportFilename(indicatorName),
      sourceWidth: chart.chartWidth,
      sourceHeight: chart.chartHeight,
    },
    {
      title: {
        text: indicatorName,
        style: {
          fontSize: '20px',
          fontWeight: '700',
          color: chartLayoutColours.titles,
        },
      },
    }
  );

  return true;
};
